// Precondition: None
// Postcondition: Custom element class for 3D button chip component with size variants (small, normal, large)

class ButtonChip extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
    this.setupEventListeners();
  }

  // Precondition: Shadow DOM attached
  // Postcondition: Renders HTML structure into shadow DOM with constructable stylesheet
  private render(): void {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(this.getStyles());

    const template = document.createElement('template');
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.adoptedStyleSheets = [sheet];
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  // Precondition: None
  // Postcondition: Returns CSS string for shadow DOM with 3D effect styles using CSS nesting
  private getStyles(): string {
    const size = this.getAttribute('size') || 'normal';
    const sizeStyles = this.getSizeStyles(size);

    return `
      :host {
        display: inline-block;
      }

      .pushable {
        position: relative;
        border: none;
        background: transparent;
        padding: 0;
        cursor: pointer;
        outline-offset: 4px;
        transition: filter 250ms;
        -webkit-tap-highlight-color: transparent;
        user-select: none;

        &:focus {
          outline: var(--focus, 2px solid #1C1C1E);

          &:not(:focus-visible) {
            outline: none;
          }
        }

        &:hover {
          filter: brightness(110%);

          & .front {
            transform: translateY(-5px);
            transition: transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1.5);
          }

          & .shadow {
            transform: translateY(3px);
            transition: transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1.5);
          }
        }

        &:active {
          & .front {
            transform: translateY(-1px);
            transition: transform 34ms;
          }

          & .shadow {
            transform: translateY(1px);
            transition: transform 34ms;
          }
        }
      }

      .shadow {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: var(--radius, 8px);
        background: hsl(0deg 0% 0% / 0.2);
        filter: blur(3px);
        will-change: transform;
        transform: translateY(2px);
        transition: transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1);
      }

      .edge {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: var(--radius, 8px);
        background: linear-gradient(
          to left,
          hsl(220deg 13% 60%) 0%,
          hsl(220deg 13% 75%) 8%,
          hsl(220deg 13% 75%) 92%,
          hsl(220deg 13% 60%) 100%
        );
      }

      .front {
        display: inline-flex;
        position: relative;
        border-radius: var(--radius, 8px);
        white-space: nowrap;
        align-items: center;
        justify-content: center;
        font-family: inherit;
        will-change: transform;
        transform: translateY(-3px);
        transition: transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1);
        background: var(--chip-bg, var(--surface, #f3f4f6));
        border: 1px solid var(--chip-border, var(--tab-border, #e5e7eb));
        color: var(--chip-text, var(--text, #1f2937));
        ${sizeStyles}
      }

      @media (prefers-reduced-motion: reduce) {
        .front,
        .shadow {
          transition: none;
        }
      }

      @media (prefers-color-scheme: dark) {
        .pushable:focus {
          outline: var(--focus, 2px solid #ffffff);
        }

        .edge {
          background: linear-gradient(
            to left,
            hsl(220deg 13% 25%) 0%,
            hsl(220deg 13% 35%) 8%,
            hsl(220deg 13% 35%) 92%,
            hsl(220deg 13% 25%) 100%
          );
        }

        .front {
          background: var(--chip-bg, var(--surface, #374151));
          border-color: var(--chip-border, var(--tab-border, #4b5563));
          color: var(--chip-text, var(--text, #f3f4f6));
        }
      }
    `;
  }

  // Precondition: size is one of 'small', 'normal', 'large'
  // Postcondition: Returns CSS string with size-specific properties
  // Uses global height variables: --height-sm, --height-md, --height-lg
  private getSizeStyles(size: string): string {
    switch (size) {
      case 'small':
        return `
          padding: 0 12px;
          font: 700 12px/1 Inter, system-ui, sans-serif;
          height: var(--height-sm, 32px);
          min-width: 48px;
        `;
      case 'large':
        return `
          padding: 0 20px;
          font: 700 16px/1 Inter, system-ui, sans-serif;
          height: var(--height-lg, 48px);
          min-width: 72px;
        `;
      case 'normal':
      default:
        return `
          padding: 0 16px;
          font: 700 14px/1 Inter, system-ui, sans-serif;
          height: var(--height-md, 40px);
          min-width: 60px;
        `;
    }
  }

  // Precondition: None
  // Postcondition: Returns HTML template string with 3D layered structure
  private getTemplate(): string {
    const label = this.getAttribute('label') || this.textContent || 'Button';
    return `
      <button class="pushable" type="button" aria-label="${label}">
        <span class="shadow"></span>
        <span class="edge"></span>
        <span class="front"><slot>${label}</slot></span>
      </button>
    `;
  }

  // Precondition: Shadow DOM rendered
  // Postcondition: Event listeners attached to button
  private setupEventListeners(): void {
    const button = this.shadowRoot?.querySelector('button');
    if (!button) return;

    button.addEventListener('click', (e) => {
      const href = this.getAttribute('href');

      // If href is set, navigate to the URL
      if (href) {
        e.preventDefault();
        window.location.href = href;
        return;
      }

      // Otherwise dispatch custom event when clicked to allow parent to handle action
      this.dispatchEvent(
        new CustomEvent('chip-click', {
          detail: { value: this.getAttribute('value') || this.textContent },
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  // Precondition: None
  // Postcondition: Returns the chip's associated value
  getValue(): string {
    return this.getAttribute('value') || this.textContent || '';
  }

  // Precondition: value is a string
  // Postcondition: Sets the chip's associated value
  setValue(value: string): void {
    this.setAttribute('value', value);
  }

  // Precondition: None
  // Postcondition: Focuses the button element
  focus(): void {
    this.shadowRoot?.querySelector('button')?.focus();
  }

  // Precondition: None
  // Postcondition: Returns true if button currently focused
  isFocused(): boolean {
    return this.shadowRoot?.activeElement === this.shadowRoot?.querySelector('button');
  }
}

// Register custom element (no export statement)

customElements.define('ft-button-chip', ButtonChip);
